import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
  FiShoppingCart, FiDownload, FiPrinter, FiRotateCcw, FiTrendingUp,
  FiTrendingDown, FiDollarSign, FiPercent, FiArrowUp, FiArrowDown
} from 'react-icons/fi';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area
} from 'recharts';
import { colors } from '../../theme/colors';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { getJobsCached, getEmployeesCached } from '../../utils/masterDataStore';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import VendorSelect from '../../components/VendorSelect';
import { formatNumber, formatDate } from '../../utils/formatters';
import useCurrency from '../../hooks/useCurrency';
import useReportMemory from '../../hooks/useReportMemory';
import { addPrintBrand } from '../../utils/printBranding';

// ── Constants ──
const CHART_COLORS = ['#800020', '#A0153E', '#5D0017', '#C4A484', '#8B7355', '#D4AF37'];
const STATUS_OPTIONS = ['All', 'Draft', 'Posted', 'Cancelled'];
// Document-type checkbox options (replaces the old Tax dropdown).
const DOC_TYPE_OPTIONS = [
  { code: 'PI', label: 'Tax Purchase' },
  { code: 'NPI', label: 'Non-Tax Purchase' },
  { code: 'IPI', label: 'Import Purchase' },
  { code: 'PR', label: 'Tax Return' },
  { code: 'NPR', label: 'Non-Tax Return' },
];
const GROUP_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' }
];

const STATUS_COLORS = {
  Draft: { bg: `${colors.neutral[500]}18`, text: colors.neutral[600] },
  Posted: { bg: `${colors.primary.main}15`, text: colors.primary.main },
  Paid: { bg: `${colors.success.main}15`, text: colors.success.main },
  Partial: { bg: `${colors.warning.main}18`, text: colors.warning.dark },
  Cancelled: { bg: `${colors.error.main}15`, text: colors.error.main }
};

// ── Helpers ──

const getWeekNumber = (d) => {
  const date = new Date(d);
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + 3 - ((date.getDay() + 6) % 7));
  const week1 = new Date(date.getFullYear(), 0, 4);
  return Math.round(((date - week1) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7) + 1;
};

const getQuarter = (month) => Math.floor(month / 3) + 1;

const getPeriodKey = (dateStr, groupBy) => {
  if (!dateStr) return 'Unknown';
  const d = new Date(dateStr);
  const year = d.getFullYear();
  const month = d.getMonth();
  switch (groupBy) {
    case 'daily':
      return dateStr;
    case 'weekly':
      return `${year}-W${String(getWeekNumber(d)).padStart(2, '0')}`;
    case 'monthly':
      return `${year}-${String(month + 1).padStart(2, '0')}`;
    case 'quarterly':
      return `${year}-Q${getQuarter(month)}`;
    default:
      return dateStr;
  }
};

const getPeriodLabel = (key, groupBy) => {
  if (!key || key === 'Unknown') return 'Unknown';
  switch (groupBy) {
    case 'daily':
      return formatDate(key, 'dd MMM yyyy');
    case 'weekly':
      return key.replace('-W', ' W');
    case 'monthly': {
      const [y, m] = key.split('-');
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${months[parseInt(m, 10) - 1]} ${y}`;
    }
    case 'quarterly':
      return key.replace('-', ' ');
    default:
      return key;
  }
};

const formatAmountLocal = (amount) => {
  const dp = (typeof window !== 'undefined' && Number.isFinite(Number(window.__AMAL_DECIMAL_PLACES__)))
    ? Number(window.__AMAL_DECIMAL_PLACES__) : 2;
  if (!amount && amount !== 0) return dp > 0 ? '0.' + '0'.repeat(dp) : '0';
  return Number(amount).toLocaleString('en-PK', {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp
  });
};

// ── Component ──

const PurchaseSummary = () => {
  const { formatCurrency } = useCurrency();
  const navigate = useNavigate();
  const { activeCompany, activeFinancialYear, activeBranch } = useAuth();
  // Backward memory: restore filters + loaded rows on Back (0 re-reads).
  const [savedMem, saveMemory] = useReportMemory('purchase-summary');
  const taxLabel = activeCompany?.taxLabel || 'GST';
  const currencyCode = activeCompany?.currencyCode || 'PKR';
  const currencySymbol = activeCompany?.currencySymbol || 'Rs.';

  // Data states
  const [transactions, setTransactions] = useState(() => savedMem?.transactions ?? []);
  const [vendors, setVendors] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [financialYears, setFinancialYears] = useState([]);
  // Restored views already have their rows — don't show the initial loading spinner.
  const [loading, setLoading] = useState(() => savedMem ? false : true);

  // Filter states
  const [selectedFYId, setSelectedFYId] = useState(() => savedMem?.selectedFYId ?? '');
  const [dateFrom, setDateFrom] = useState(() => savedMem?.dateFrom ?? '');
  const [dateTo, setDateTo] = useState(() => savedMem?.dateTo ?? '');
  const [selectedVendor, setSelectedVendor] = useState(() => savedMem?.selectedVendor ?? 'all');
  const [selectedJob, setSelectedJob] = useState(() => savedMem?.selectedJob ?? 'all');
  const [selectedEmployee, setSelectedEmployee] = useState(() => savedMem?.selectedEmployee ?? 'all');
  // FLW-05: default to posted documents (Posted / Partial / Paid) — Draft and
  // Cancelled are left out unless picked in the Status filter.
  const [statusFilter, setStatusFilter] = useState(() => savedMem?.statusFilter ?? 'Posted');
  const [docTypes, setDocTypes] = useState(() => savedMem?.docTypes ?? { PI: true, NPI: true, IPI: true, PR: true, NPR: true });
  const toggleDocType = (code) => setDocTypes(prev => ({ ...prev, [code]: !prev[code] }));
  const [groupBy, setGroupBy] = useState(() => savedMem?.groupBy ?? 'monthly');

  // Sort states
  const [sortColumn, setSortColumn] = useState(() => savedMem?.sortColumn ?? 'docDate');
  const [sortDirection, setSortDirection] = useState(() => savedMem?.sortDirection ?? 'desc');

  // ── Load Financial Years ──
  const loadFinancialYears = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const fyRef = collection(db, 'companies', activeCompany.id, 'financialYears');
      const q = query(fyRef, orderBy('startDate', 'desc'));
      const snapshot = await getDocs(q);
      const fys = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setFinancialYears(fys);

      // On Back (restored), keep the restored FY — don't overwrite it with the active FY.
      if (!savedMem) {
        if (activeFinancialYear?.id) {
          setSelectedFYId(activeFinancialYear.id);
        } else if (fys.length > 0) {
          setSelectedFYId(fys[0].id);
        }
      }
    } catch (error) {
      console.error('Error loading financial years:', error);
    }
  }, [activeCompany, activeFinancialYear, savedMem]);

  // ── Load Vendors ──
  const loadVendors = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const ref = collection(db, 'companies', activeCompany.id, 'vendors');
      const q = query(ref, orderBy('vendorName'));
      const snapshot = await getDocs(q);
      setVendors(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Error loading vendors:', error);
    }
  }, [activeCompany]);

  // ── Load Jobs ──
  const loadJobs = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const jobRows = await getJobsCached(activeCompany.id);
      setJobs(jobRows.filter(r => r.jobName != null)
        .sort((a, b) => String(a.jobName || '').localeCompare(String(b.jobName || ''))));
    } catch (error) {
      console.error('Error loading jobs:', error);
    }
  }, [activeCompany]);

  // ── Load Employees ──
  const loadEmployees = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const empRows = await getEmployeesCached(activeCompany.id);
      setEmployees(empRows.filter(r => r.employeeName != null)
        .sort((a, b) => String(a.employeeName || '').localeCompare(String(b.employeeName || ''))));
    } catch (error) {
      console.error('Error loading employees:', error);
    }
  }, [activeCompany]);

  // ── Load Purchase Transactions ──
  const loadTransactions = useCallback(async () => {
    if (!activeCompany?.id || !selectedFYId) return;
    try {
      setLoading(true);
      const ref = collection(db, 'companies', activeCompany.id, 'transactions');

      const conditions = [
        where('tranTypeCode', 'in', ['PI', 'NPI', 'IPI', 'PR', 'NPR']),
        where('financialYearId', '==', selectedFYId)
      ];
      // Server-side date bounds (Firebase cost): fetch only the picked range
      // instead of the whole FY. Empty pickers = whole FY (old behavior).
      // Client-side date filter below stays as a harmless second pass.
      if (dateFrom) conditions.push(where('docDate', '>=', dateFrom));
      if (dateTo) conditions.push(where('docDate', '<=', dateTo));

      const q = query(ref, ...conditions);
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(t => {
          if (!activeBranch || activeBranch.id === 'all') return true;
          return t.branchId === activeBranch.id;
        });

      // Sort by docDate descending by default
      data.sort((a, b) => (b.docDate || '').localeCompare(a.docDate || ''));
      setTransactions(data);
    } catch (error) {
      console.error('Error loading purchase transactions:', error);
    } finally {
      setLoading(false);
    }
  }, [activeCompany, selectedFYId, activeBranch, dateFrom, dateTo]);

  useEffect(() => { loadFinancialYears(); }, [loadFinancialYears]);
  useEffect(() => { loadVendors(); }, [loadVendors]);
  useEffect(() => { loadJobs(); }, [loadJobs]);
  useEffect(() => { loadEmployees(); }, [loadEmployees]);
  // On Back (restored), SKIP ONLY the first auto-load — restored rows already hold
  // the data (0 re-reads) — but still let a later Financial-Year change re-fetch.
  const skipInitialLoad = useRef(!!savedMem);
  useEffect(() => {
    if (skipInitialLoad.current) { skipInitialLoad.current = false; return; }
    if (selectedFYId) loadTransactions();
  }, [loadTransactions, selectedFYId]);

  // Remember this view (filters + loaded rows) once loaded, so Back restores it
  // with 0 re-reads. Filters here are display-only over already-loaded data, so
  // keep the snapshot in sync as the user tweaks them (in-memory, ~free).
  useEffect(() => {
    if (loading) return;
    saveMemory({
      transactions,
      selectedFYId, dateFrom, dateTo, selectedVendor, selectedJob,
      selectedEmployee, statusFilter, docTypes, groupBy,
      sortColumn, sortDirection
    });
  }, [loading, transactions, selectedFYId, dateFrom, dateTo, selectedVendor,
      selectedJob, selectedEmployee, statusFilter, docTypes, groupBy,
      sortColumn, sortDirection, saveMemory]);

  // ── Filtered Data ──
  const filteredData = useMemo(() => {
    let data = [...transactions];

    if (dateFrom) {
      data = data.filter(t => (t.docDate || '') >= dateFrom);
    }
    if (dateTo) {
      data = data.filter(t => (t.docDate || '') <= dateTo);
    }
    if (selectedVendor !== 'all') {
      data = data.filter(t => t.partyId === selectedVendor);
    }
    if (selectedJob !== 'all') {
      data = data.filter(t => t.jobId === selectedJob);
    }
    if (selectedEmployee !== 'all') {
      data = data.filter(t => t.employeeId === selectedEmployee);
    }
    if (statusFilter !== 'All') {
      const wanted = statusFilter === 'Posted' ? ['Posted', 'Paid', 'Partial'] : [statusFilter];
      data = data.filter(t => wanted.includes(t.status));
    }

    // Document-type checkboxes
    data = data.filter(t => docTypes[t.tranTypeCode]);

    return data;
  }, [transactions, dateFrom, dateTo, selectedVendor, selectedJob, selectedEmployee, statusFilter, docTypes]);

  // ── Sorted Data ──
  const sortedData = useMemo(() => {
    const data = [...filteredData];
    data.sort((a, b) => {
      let valA, valB;
      switch (sortColumn) {
        case 'docDate':
          valA = a.docDate || '';
          valB = b.docDate || '';
          break;
        case 'docNumber':
          valA = a.docNumber || '';
          valB = b.docNumber || '';
          break;
        case 'vendorInvoiceNo':
          valA = a.vendorInvoiceNo || '';
          valB = b.vendorInvoiceNo || '';
          break;
        case 'partyName':
          valA = (a.partyName || '').toLowerCase();
          valB = (b.partyName || '').toLowerCase();
          break;
        case 'jobName':
          valA = (a.jobName || '').toLowerCase();
          valB = (b.jobName || '').toLowerCase();
          break;
        case 'employeeName':
          valA = (a.employeeName || '').toLowerCase();
          valB = (b.employeeName || '').toLowerCase();
          break;
        case 'totalAmount':
          valA = a.totalAmount || 0;
          valB = b.totalAmount || 0;
          break;
        case 'totalDiscount':
          valA = a.totalDiscount || 0;
          valB = b.totalDiscount || 0;
          break;
        case 'totalGST':
          valA = a.totalGST || 0;
          valB = b.totalGST || 0;
          break;
        case 'totalWHT':
          valA = a.totalWHT || 0;
          valB = b.totalWHT || 0;
          break;
        case 'netAmount':
          valA = a.netAmount || 0;
          valB = b.netAmount || 0;
          break;
        case 'status':
          valA = a.status || '';
          valB = b.status || '';
          break;
        default:
          valA = a.docDate || '';
          valB = b.docDate || '';
      }
      if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
      if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return data;
  }, [filteredData, sortColumn, sortDirection]);

  // ── Summary Calculations ──
  const summary = useMemo(() => {
    const piTransactions = filteredData.filter(t => ['PI', 'NPI'].includes(t.tranTypeCode));
    const prTransactions = filteredData.filter(t => ['PR', 'NPR'].includes(t.tranTypeCode));

    const totalPurchases = piTransactions.reduce((sum, t) => sum + (t.netAmount || 0), 0);
    const totalReturns = prTransactions.reduce((sum, t) => sum + (t.netAmount || 0), 0);
    const netPurchases = totalPurchases - totalReturns;
    const totalGST = filteredData.reduce((sum, t) => {
      const gst = t.totalGST || 0;
      return ['PR', 'NPR'].includes(t.tranTypeCode) ? sum - gst : sum + gst;
    }, 0);
    const totalWHT = filteredData.reduce((sum, t) => {
      const wht = t.totalWHT || 0;
      return ['PR', 'NPR'].includes(t.tranTypeCode) ? sum - wht : sum + wht;
    }, 0);
    const avgPurchaseValue = piTransactions.length > 0
      ? totalPurchases / piTransactions.length
      : 0;

    return {
      totalPurchases,
      totalReturns,
      netPurchases,
      totalGST,
      totalWHT,
      avgPurchaseValue,
      piCount: piTransactions.length,
      prCount: prTransactions.length,
      totalCount: filteredData.length
    };
  }, [filteredData]);

  // ── Chart Data: Purchase Trend (Area) ──
  const trendChartData = useMemo(() => {
    const periodMap = {};

    filteredData.forEach(t => {
      const key = getPeriodKey(t.docDate, groupBy);
      if (!periodMap[key]) {
        periodMap[key] = { period: key, purchases: 0, returns: 0, net: 0 };
      }
      const amount = t.netAmount || 0;
      if (['PI', 'NPI'].includes(t.tranTypeCode)) {
        periodMap[key].purchases += amount;
      } else if (['PR', 'NPR'].includes(t.tranTypeCode)) {
        periodMap[key].returns += amount;
      }
    });

    const sorted = Object.values(periodMap).sort((a, b) => a.period.localeCompare(b.period));
    sorted.forEach(item => {
      item.net = item.purchases - item.returns;
      item.label = getPeriodLabel(item.period, groupBy);
    });

    return sorted;
  }, [filteredData, groupBy]);

  // ── Chart Data: Top 10 Vendors (Bar) ──
  const vendorBarChartData = useMemo(() => {
    const vendorMap = {};
    filteredData.forEach(t => {
      const name = t.partyName || 'Unknown';
      if (!vendorMap[name]) {
        vendorMap[name] = { vendor: name, amount: 0 };
      }
      const amt = t.netAmount || 0;
      if (['PI', 'NPI'].includes(t.tranTypeCode)) {
        vendorMap[name].amount += amt;
      } else if (['PR', 'NPR'].includes(t.tranTypeCode)) {
        vendorMap[name].amount -= amt;
      }
    });

    return Object.values(vendorMap)
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 10)
      .map(item => ({
        ...item,
        vendor: item.vendor.length > 18 ? item.vendor.substring(0, 18) + '...' : item.vendor
      }));
  }, [filteredData]);

  // ── Chart Data: Vendor Distribution (Pie) ──
  const vendorPieChartData = useMemo(() => {
    const vendorMap = {};
    filteredData.forEach(t => {
      if (!['PI', 'NPI'].includes(t.tranTypeCode)) return;
      const name = t.partyName || 'Unknown';
      if (!vendorMap[name]) {
        vendorMap[name] = { name, value: 0 };
      }
      vendorMap[name].value += t.netAmount || 0;
    });

    const sorted = Object.values(vendorMap).sort((a, b) => b.value - a.value);
    if (sorted.length <= 6) return sorted;

    const top5 = sorted.slice(0, 5);
    const othersTotal = sorted.slice(5).reduce((sum, v) => sum + v.value, 0);
    return [...top5, { name: 'Others', value: othersTotal }];
  }, [filteredData]);

  // ── Sort Handler ──
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const renderSortIcon = (column) => {
    if (sortColumn !== column) return null;
    return sortDirection === 'asc'
      ? <FiArrowUp size={11} style={{ marginLeft: 4 }} />
      : <FiArrowDown size={11} style={{ marginLeft: 4 }} />;
  };

  // ── Navigation ──
  const handleDocClick = (transaction) => {
    const route = ['PI', 'NPI'].includes(transaction.tranTypeCode)
      ? '/purchases/purchase-invoice'
      : '/purchases/purchase-return';
    navigate(route, { state: { viewTransactionId: transaction.id } });
  };

  // ── Reset Filters ──
  const handleResetFilters = () => {
    setDateFrom('');
    setDateTo('');
    setSelectedVendor('all');
    setSelectedJob('all');
    setSelectedEmployee('all');
    setStatusFilter('Posted');
    setGroupBy('monthly');
    if (activeFinancialYear?.id) {
      setSelectedFYId(activeFinancialYear.id);
    }
  };

  // ── CSV Export ──
  const handleExport = () => {
    const selectedFY = financialYears.find(f => f.id === selectedFYId);
    let csv = 'Purchase Summary Report\n';
    csv += `Company: ${activeCompany?.companyName || ''}\n`;
    csv += `Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || 'All'}\n`;
    csv += `Date Range: ${dateFrom || 'Start'} to ${dateTo || 'End'}\n`;
    csv += `Status: ${statusFilter}\n\n`;

    csv += `Date,Doc #,Vendor Invoice,Vendor,Job,Employee,Gross Amount,Discount,${taxLabel},WHT,Net Amount,Status\n`;

    sortedData.forEach(t => {
      csv += `"${formatDate(t.docDate)}",`;
      csv += `"${t.docNumber || ''}",`;
      csv += `"${(t.vendorInvoiceNo || '').replace(/"/g, '""')}",`;
      csv += `"${(t.partyName || '').replace(/"/g, '""')}",`;
      csv += `"${(t.jobName || '').replace(/"/g, '""')}",`;
      csv += `"${(t.employeeName || '').replace(/"/g, '""')}",`;
      csv += `${t.totalAmount || 0},`;
      csv += `${t.totalDiscount || 0},`;
      csv += `${t.totalGST || 0},`;
      csv += `${t.totalWHT || 0},`;
      csv += `${t.netAmount || 0},`;
      csv += `"${t.status || ''}"\n`;
    });

    csv += `\nTotal Purchases (PI):,${summary.totalPurchases}\n`;
    csv += `Total Returns (PR):,${summary.totalReturns}\n`;
    csv += `Net Purchases:,${summary.netPurchases}\n`;
    csv += `Total ${taxLabel}:,${summary.totalGST}\n`;
    csv += `Total WHT:,${summary.totalWHT}\n`;

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Purchase_Summary_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // ── Print ──
  const handlePrint = () => {
    const selectedFY = financialYears.find(f => f.id === selectedFYId);
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Purchase Summary</title>
      <style>
        body{font-family:'Segoe UI',sans-serif;margin:30px;color:#1A1A1A;font-size:13px}
        h1{color:#800020;font-size:20px;margin-bottom:5px}
        .meta{color:#666;font-size:12px;margin-bottom:20px}
        .summary-row{display:flex;gap:20px;margin-bottom:20px;flex-wrap:wrap}
        .summary-box{border:1px solid #ddd;border-radius:6px;padding:10px 16px;min-width:140px}
        .summary-box .label{font-size:11px;color:#666;text-transform:uppercase}
        .summary-box .value{font-size:18px;font-weight:600;color:#1A1A1A}
        table{width:100%;border-collapse:collapse;margin-top:10px}
        th{background:#800020;color:white;padding:8px 10px;text-align:left;font-size:11px;text-transform:uppercase}
        th.right{text-align:right}
        td{padding:6px 10px;border-bottom:1px solid #eee;font-size:12px}
        td.right{text-align:right;font-family:'Consolas',monospace}
        .totals-row{background:#f5f5f5;font-weight:700}
        .totals-row td{border-top:2px solid #800020;padding:10px}
        .status{display:inline-block;padding:2px 8px;border-radius:3px;font-size:10px;font-weight:600}
        .status-Posted{background:#80002015;color:#800020}
        .status-Paid{background:#28A74515;color:#28A745}
        .status-Partial{background:#FFC10718;color:#FFA000}
        .status-Draft{background:#ADB5BD18;color:#6C757D}
        .status-Cancelled{background:#DC354515;color:#DC3545}
        @media print{body{margin:15px}}
      </style></head><body>
      <h1>Purchase Summary Report</h1>
      <div class="meta">
        ${activeCompany?.companyName || 'Company'} |
        Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || 'All'} |
        Date Range: ${dateFrom || 'Start'} to ${dateTo || 'End'} |
        Status: ${statusFilter} |
        Total Records: ${sortedData.length}
      </div>
      <div class="summary-row">
        <div class="summary-box"><div class="label">Total Purchases</div><div class="value">${currencySymbol}${formatAmountLocal(summary.totalPurchases)}</div></div>
        <div class="summary-box"><div class="label">Total Returns</div><div class="value">${currencySymbol}${formatAmountLocal(summary.totalReturns)}</div></div>
        <div class="summary-box"><div class="label">Net Purchases</div><div class="value">${currencySymbol}${formatAmountLocal(summary.netPurchases)}</div></div>
        <div class="summary-box"><div class="label">Total ${taxLabel}</div><div class="value">${currencySymbol}${formatAmountLocal(summary.totalGST)}</div></div>
        <div class="summary-box"><div class="label">Total WHT</div><div class="value">${currencySymbol}${formatAmountLocal(summary.totalWHT)}</div></div>
        <div class="summary-box"><div class="label">Avg Purchase</div><div class="value">${currencySymbol}${formatAmountLocal(summary.avgPurchaseValue)}</div></div>
      </div>
      <table>
        <thead><tr>
          <th>Date</th><th>Doc #</th><th>Vendor Invoice</th><th>Vendor</th><th>Job</th><th>Employee</th>
          <th class="right">Gross</th><th class="right">Disc.</th><th class="right">${taxLabel}</th>
          <th class="right">WHT</th><th class="right">Net Amount</th><th>Status</th>
        </tr></thead>
        <tbody>`);

    sortedData.forEach(t => {
      w.document.write(`
        <tr>
          <td>${formatDate(t.docDate)}</td>
          <td>${t.docNumber || ''}</td>
          <td>${t.vendorInvoiceNo || '-'}</td>
          <td>${t.partyName || ''}</td>
          <td>${t.jobName || '-'}</td>
          <td>${t.employeeName || '-'}</td>
          <td class="right">${formatAmountLocal(t.totalAmount)}</td>
          <td class="right">${formatAmountLocal(t.totalDiscount)}</td>
          <td class="right">${formatAmountLocal(t.totalGST)}</td>
          <td class="right">${formatAmountLocal(t.totalWHT)}</td>
          <td class="right">${formatAmountLocal(t.netAmount)}</td>
          <td><span class="status status-${t.status || 'Draft'}">${t.status || 'Draft'}</span></td>
        </tr>`);
    });

    // Totals row
    const totGross = sortedData.reduce((s, t) => s + (t.totalAmount || 0), 0);
    const totDisc = sortedData.reduce((s, t) => s + (t.totalDiscount || 0), 0);
    const totGST = sortedData.reduce((s, t) => s + (t.totalGST || 0), 0);
    const totWHT = sortedData.reduce((s, t) => s + (t.totalWHT || 0), 0);
    const totNet = sortedData.reduce((s, t) => s + (t.netAmount || 0), 0);

    w.document.write(`
        <tr class="totals-row">
          <td colspan="6" style="text-align:right">TOTAL</td>
          <td class="right">${formatAmountLocal(totGross)}</td>
          <td class="right">${formatAmountLocal(totDisc)}</td>
          <td class="right">${formatAmountLocal(totGST)}</td>
          <td class="right">${formatAmountLocal(totWHT)}</td>
          <td class="right">${formatAmountLocal(totNet)}</td>
          <td></td>
        </tr>
        </tbody></table>
        <div style="margin-top:30px;text-align:center;color:#999;font-size:11px">
          Generated on ${new Date().toLocaleString()} | AMAL ERP
        </div>
      </body></html>`);
    addPrintBrand(w);
    w.document.close();
    setTimeout(() => w.print(), 500);
  };

  // ── Totals Row for Table ──
  const tableTotals = useMemo(() => {
    return {
      totalAmount: sortedData.reduce((s, t) => s + (t.totalAmount || 0), 0),
      totalDiscount: sortedData.reduce((s, t) => s + (t.totalDiscount || 0), 0),
      totalGST: sortedData.reduce((s, t) => s + (t.totalGST || 0), 0),
      totalWHT: sortedData.reduce((s, t) => s + (t.totalWHT || 0), 0),
      netAmount: sortedData.reduce((s, t) => s + (t.netAmount || 0), 0)
    };
  }, [sortedData]);

  // ── Custom Tooltip for Charts ──
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    return (
      <ChartTooltipWrapper>
        <ChartTooltipLabel>{label}</ChartTooltipLabel>
        {payload.map((entry, i) => (
          <ChartTooltipRow key={i}>
            <ChartTooltipDot style={{ background: entry.color }} />
            <span>{entry.name}:</span>
            <strong>{formatCurrency(entry.value)}</strong>
          </ChartTooltipRow>
        ))}
      </ChartTooltipWrapper>
    );
  };

  const PieCustomTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const data = payload[0];
    const total = vendorPieChartData.reduce((s, d) => s + d.value, 0);
    const pct = total > 0 ? ((data.value / total) * 100).toFixed(1) : 0;
    return (
      <ChartTooltipWrapper>
        <ChartTooltipLabel>{data.name}</ChartTooltipLabel>
        <ChartTooltipRow>
          <span>Amount:</span>
          <strong>{formatCurrency(data.value)}</strong>
        </ChartTooltipRow>
        <ChartTooltipRow>
          <span>Share:</span>
          <strong>{pct}%</strong>
        </ChartTooltipRow>
      </ChartTooltipWrapper>
    );
  };

  // ── Render ──
  return (
    <PageLayout pageTitle="Purchase Summary" breadcrumb="Reports / Purchase Reports / Purchase Summary">
      {/* Summary Cards */}
      <CardsGrid>
        <SummaryCard>
          <CardIcon color={colors.primary.main}>
            <FiShoppingCart />
          </CardIcon>
          <CardInfo>
            <CardLabel>Total Purchases</CardLabel>
            <CardValue>{formatCurrency(summary.totalPurchases)}</CardValue>
            <CardSub>{summary.piCount} invoice{summary.piCount !== 1 ? 's' : ''}</CardSub>
          </CardInfo>
        </SummaryCard>

        <SummaryCard>
          <CardIcon color={colors.error.main}>
            <FiRotateCcw />
          </CardIcon>
          <CardInfo>
            <CardLabel>Total Returns</CardLabel>
            <CardValue>{formatCurrency(summary.totalReturns)}</CardValue>
            <CardSub>{summary.prCount} return{summary.prCount !== 1 ? 's' : ''}</CardSub>
          </CardInfo>
        </SummaryCard>

        <SummaryCard>
          <CardIcon color={colors.success.main}>
            <FiTrendingUp />
          </CardIcon>
          <CardInfo>
            <CardLabel>Net Purchases</CardLabel>
            <CardValue>{formatCurrency(summary.netPurchases)}</CardValue>
            <CardSub>PI - PR</CardSub>
          </CardInfo>
        </SummaryCard>

        <SummaryCard>
          <CardIcon color={colors.warning.main}>
            <FiPercent />
          </CardIcon>
          <CardInfo>
            <CardLabel>Total {taxLabel}</CardLabel>
            <CardValue>{formatCurrency(summary.totalGST)}</CardValue>
            <CardSub>Input tax</CardSub>
          </CardInfo>
        </SummaryCard>

        <SummaryCard>
          <CardIcon color={colors.info.main}>
            <FiTrendingDown />
          </CardIcon>
          <CardInfo>
            <CardLabel>Total WHT Deducted</CardLabel>
            <CardValue>{formatCurrency(summary.totalWHT)}</CardValue>
            <CardSub>Withholding tax</CardSub>
          </CardInfo>
        </SummaryCard>

        <SummaryCard>
          <CardIcon color="#8B7355">
            <FiDollarSign />
          </CardIcon>
          <CardInfo>
            <CardLabel>Avg Purchase Value</CardLabel>
            <CardValue>{formatCurrency(summary.avgPurchaseValue)}</CardValue>
            <CardSub>Per invoice</CardSub>
          </CardInfo>
        </SummaryCard>
      </CardsGrid>

      {/* Filters */}
      <FiltersCard>
        <FiltersRow>
          <FilterGroup>
            <Label>Financial Year</Label>
            <Select
              value={selectedFYId}
              onChange={(e) => setSelectedFYId(e.target.value)}
            >
              <option value="">-- Select Year --</option>
              {financialYears.map(fy => (
                <option key={fy.id} value={fy.id}>
                  {fy.yearName || fy.yearCode}
                </option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Date From</Label>
            <DateInput
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </FilterGroup>

          <FilterGroup>
            <Label>Date To</Label>
            <DateInput
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </FilterGroup>

          <FilterGroup>
            <Label>Vendor</Label>
            <VendorSelect
              vendors={vendors}
              selectedVendorId={selectedVendor}
              onChange={setSelectedVendor}
              allOption={{ value: 'all', label: 'All Vendors' }}
            />
          </FilterGroup>

          <FilterGroup>
            <Label>Job / Project</Label>
            <Select
              value={selectedJob}
              onChange={(e) => setSelectedJob(e.target.value)}
            >
              <option value="all">All Jobs</option>
              {jobs.map(j => (
                <option key={j.id} value={j.id}>
                  {j.jobName}
                </option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Employee</Label>
            <Select
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
            >
              <option value="all">All Employees</option>
              {employees.map(emp => (
                <option key={emp.id} value={emp.id}>
                  {emp.employeeName || emp.name}
                </option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Status</Label>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="All">All Statuses</option>
              <option value="Draft">Draft</option>
              <option value="Posted">Posted (incl. Paid / Partial)</option>
              <option value="Cancelled">Cancelled</option>
            </Select>
          </FilterGroup>

          <FilterGroup style={{ gridColumn: 'span 2' }}>
            <Label>Document Types</Label>
            <DocTypeChecks>
              {DOC_TYPE_OPTIONS.map(o => (
                <DocCheck key={o.code}>
                  <input type="checkbox" checked={!!docTypes[o.code]} onChange={() => toggleDocType(o.code)} />
                  <span>{o.label}</span>
                </DocCheck>
              ))}
            </DocTypeChecks>
          </FilterGroup>

          <FilterGroup>
            <Label>Group By</Label>
            <Select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
            >
              {GROUP_OPTIONS.map(g => (
                <option key={g.value} value={g.value}>{g.label}</option>
              ))}
            </Select>
          </FilterGroup>

          <ButtonGroup>
            <Button onClick={handleResetFilters} title="Reset Filters">
              <FiRotateCcw /> Reset
            </Button>
            <Button onClick={handleExport}>
              <FiDownload /> CSV
            </Button>
            <Button onClick={handlePrint}>
              <FiPrinter /> Print
            </Button>
          </ButtonGroup>
        </FiltersRow>
      </FiltersCard>

      {/* Data Table */}
      <ContentCard>
        <TableHeader>
          <TableHeaderTitle>
            Purchase Transactions
            <TableCount>{sortedData.length} record{sortedData.length !== 1 ? 's' : ''}</TableCount>
          </TableHeaderTitle>
        </TableHeader>
        <TableContainer>
          {loading ? (
            <EmptyState>
              <LoadingSpinner />
              <EmptyText>Loading purchase data...</EmptyText>
            </EmptyState>
          ) : sortedData.length === 0 ? (
            <EmptyState>
              <EmptyIcon><FiShoppingCart /></EmptyIcon>
              <EmptyText>
                {transactions.length === 0
                  ? 'No purchase transactions found for this financial year'
                  : 'No transactions match the selected filters'}
              </EmptyText>
              <EmptySubText>Try adjusting your filters or selecting a different financial year</EmptySubText>
            </EmptyState>
          ) : (
            <Table>
              <Thead>
                <tr>
                  <Th sortable onClick={() => handleSort('docDate')}>
                    Date {renderSortIcon('docDate')}
                  </Th>
                  <Th sortable onClick={() => handleSort('docNumber')}>
                    Doc # {renderSortIcon('docNumber')}
                  </Th>
                  <Th sortable onClick={() => handleSort('vendorInvoiceNo')}>
                    Vendor Invoice {renderSortIcon('vendorInvoiceNo')}
                  </Th>
                  <Th sortable onClick={() => handleSort('partyName')}>
                    Vendor {renderSortIcon('partyName')}
                  </Th>
                  <Th sortable onClick={() => handleSort('jobName')}>
                    Job {renderSortIcon('jobName')}
                  </Th>
                  <Th sortable onClick={() => handleSort('employeeName')}>
                    Employee {renderSortIcon('employeeName')}
                  </Th>
                  <Th align="right" sortable onClick={() => handleSort('totalAmount')}>
                    Gross Amount {renderSortIcon('totalAmount')}
                  </Th>
                  <Th align="right" sortable onClick={() => handleSort('totalDiscount')}>
                    Discount {renderSortIcon('totalDiscount')}
                  </Th>
                  <Th align="right" sortable onClick={() => handleSort('totalGST')}>
                    {taxLabel} {renderSortIcon('totalGST')}
                  </Th>
                  <Th align="right" sortable onClick={() => handleSort('totalWHT')}>
                    WHT {renderSortIcon('totalWHT')}
                  </Th>
                  <Th align="right" sortable onClick={() => handleSort('netAmount')}>
                    Net Amount {renderSortIcon('netAmount')}
                  </Th>
                  <Th align="center" sortable onClick={() => handleSort('status')}>
                    Status {renderSortIcon('status')}
                  </Th>
                </tr>
              </Thead>
              <Tbody>
                {sortedData.map(t => (
                  <Tr key={t.id}>
                    <Td>{formatDate(t.docDate)}</Td>
                    <Td>
                      <DocLink onClick={() => handleDocClick(t)}>
                        {t.docNumber || '-'}
                      </DocLink>
                      <TranTypeBadge $type={t.tranTypeCode}>
                        {t.tranTypeCode}
                      </TranTypeBadge>
                    </Td>
                    <Td>{t.vendorInvoiceNo || '-'}</Td>
                    <Td title={t.partyName}>{t.partyName || '-'}</Td>
                    <Td>{t.jobName || '-'}</Td>
                    <Td>{t.employeeName || '-'}</Td>
                    <AmountTd align="right">{formatAmountLocal(t.totalAmount)}</AmountTd>
                    <AmountTd align="right">{formatAmountLocal(t.totalDiscount)}</AmountTd>
                    <AmountTd align="right">{formatAmountLocal(t.totalGST)}</AmountTd>
                    <AmountTd align="right">{formatAmountLocal(t.totalWHT)}</AmountTd>
                    <AmountTd align="right" $highlight>
                      {formatAmountLocal(t.netAmount)}
                    </AmountTd>
                    <Td align="center">
                      <StatusBadge $status={t.status || 'Draft'}>
                        {t.status || 'Draft'}
                      </StatusBadge>
                    </Td>
                  </Tr>
                ))}

                {/* Totals Row */}
                <TotalsRow>
                  <Td colSpan={6} style={{ textAlign: 'right', fontWeight: 700 }}>
                    TOTAL ({sortedData.length} records)
                  </Td>
                  <AmountTd align="right">{formatAmountLocal(tableTotals.totalAmount)}</AmountTd>
                  <AmountTd align="right">{formatAmountLocal(tableTotals.totalDiscount)}</AmountTd>
                  <AmountTd align="right">{formatAmountLocal(tableTotals.totalGST)}</AmountTd>
                  <AmountTd align="right">{formatAmountLocal(tableTotals.totalWHT)}</AmountTd>
                  <AmountTd align="right" $highlight>{formatAmountLocal(tableTotals.netAmount)}</AmountTd>
                  <Td />
                </TotalsRow>
              </Tbody>
            </Table>
          )}
        </TableContainer>
      </ContentCard>

      {/* Charts */}
      {!loading && filteredData.length > 0 && (
        <ChartsGrid>
          {/* Area Chart: Purchase Trend */}
          <ChartCard $span={2}>
            <ChartTitle>Purchase Trend ({GROUP_OPTIONS.find(g => g.value === groupBy)?.label || 'Monthly'})</ChartTitle>
            {trendChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={trendChartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gradPurchases" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#800020" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#800020" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gradReturns" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#DC3545" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#DC3545" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E9ECEF" />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11, fill: colors.text.secondary }}
                    axisLine={{ stroke: '#DEE2E6' }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: colors.text.secondary }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => formatCurrency(v, { compact: true })}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Area
                    type="monotone"
                    dataKey="purchases"
                    name="Purchases"
                    stroke="#800020"
                    strokeWidth={2}
                    fill="url(#gradPurchases)"
                  />
                  <Area
                    type="monotone"
                    dataKey="returns"
                    name="Returns"
                    stroke="#DC3545"
                    strokeWidth={2}
                    fill="url(#gradReturns)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <ChartEmpty>No trend data available</ChartEmpty>
            )}
          </ChartCard>

          {/* Bar Chart: Top 10 Vendors */}
          <ChartCard>
            <ChartTitle>Top 10 Vendors by Purchase Amount</ChartTitle>
            {vendorBarChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={vendorBarChartData} layout="vertical" margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E9ECEF" horizontal={false} />
                  <XAxis
                    type="number"
                    tick={{ fontSize: 10, fill: colors.text.secondary }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => formatCurrency(v, { compact: true })}
                  />
                  <YAxis
                    type="category"
                    dataKey="vendor"
                    tick={{ fontSize: 10, fill: colors.text.secondary }}
                    axisLine={false}
                    tickLine={false}
                    width={130}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="amount" name="Net Purchase" fill="#800020" radius={[0, 4, 4, 0]} barSize={20} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <ChartEmpty>No vendor data available</ChartEmpty>
            )}
          </ChartCard>

          {/* Pie Chart: Vendor Distribution */}
          <ChartCard>
            <ChartTitle>Purchase Distribution by Vendor</ChartTitle>
            {vendorPieChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={vendorPieChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={100}
                    paddingAngle={3}
                    dataKey="value"
                    nameKey="name"
                    label={({ name, percent }) =>
                      `${name.length > 12 ? name.substring(0, 12) + '..' : name} (${(percent * 100).toFixed(0)}%)`
                    }
                    labelLine={{ stroke: colors.text.secondary, strokeWidth: 1 }}
                  >
                    {vendorPieChartData.map((_, idx) => (
                      <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<PieCustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <ChartEmpty>No vendor data available</ChartEmpty>
            )}
          </ChartCard>
        </ChartsGrid>
      )}
    </PageLayout>
  );
};

// ══════════════════════════════════════════════════════════════
//  Styled Components
// ══════════════════════════════════════════════════════════════

// ── Summary Cards ──

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
`;

const SummaryCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: box-shadow 0.2s ease, transform 0.2s ease;

  &:hover {
    box-shadow: ${colors.shadows.md};
    transform: translateY(-1px);
  }
`;

const CardIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${colors.borderRadius.md};
  background: ${props => props.color || colors.primary.main}15;
  color: ${props => props.color || colors.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
`;

const CardInfo = styled.div`
  min-width: 0;
  flex: 1;
`;

const CardLabel = styled.div`
  font-size: 12px;
  color: ${colors.text.secondary};
  margin-bottom: 4px;
  text-transform: uppercase;
  font-weight: 600;
`;

const CardValue = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: ${colors.text.primary};
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardSub = styled.div`
  font-size: 11px;
  color: ${colors.text.tertiary};
  margin-top: 2px;
`;

// ── Filters ──

const FiltersCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  margin-bottom: 20px;
`;

const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  align-items: flex-end;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 160px;
`;

const Label = styled.label`
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const DocTypeChecks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px 14px;
  padding: 8px 10px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  background: white;
`;

const DocCheck = styled.label`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 13px;
  color: ${colors.text.primary};
  cursor: pointer;
  white-space: nowrap;
  input { cursor: pointer; }
`;

const Select = styled.select`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  background: white;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 0;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const DateInput = styled.input`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  background: white;
  transition: all 0.2s;
  min-width: 140px;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  margin-left: auto;
`;

const Button = styled.button`
  padding: 9px 16px;
  border: none;
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
  background: ${colors.neutral[100]};
  color: ${colors.text.primary};
  white-space: nowrap;

  &:hover {
    background: ${colors.neutral[200]};
  }
`;

// ── Charts ──

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  grid-column: ${props => props.$span ? `span ${props.$span}` : 'span 1'};

  @media (max-width: 1024px) {
    grid-column: span 1;
  }
`;

const ChartTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin-bottom: 16px;
`;

const ChartEmpty = styled.div`
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.text.tertiary};
  font-size: 14px;
`;

const ChartTooltipWrapper = styled.div`
  background: white;
  border: 1px solid ${colors.neutral[200]};
  border-radius: ${colors.borderRadius.md};
  box-shadow: ${colors.shadows.md};
  padding: 10px 14px;
  font-size: 12px;
`;

const ChartTooltipLabel = styled.div`
  font-weight: 600;
  color: ${colors.text.primary};
  margin-bottom: 6px;
  font-size: 12px;
`;

const ChartTooltipRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${colors.text.secondary};
  margin-bottom: 2px;

  strong {
    color: ${colors.text.primary};
    margin-left: auto;
  }
`;

const ChartTooltipDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
`;

// ── Table ──

const ContentCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  overflow: hidden;
`;

const TableHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid ${colors.neutral[100]};
`;

const TableHeaderTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${colors.text.primary};
  display: flex;
  align-items: center;
  gap: 10px;
`;

const TableCount = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: ${colors.text.secondary};
  background: ${colors.neutral[100]};
  padding: 3px 10px;
  border-radius: 12px;
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: ${colors.neutral[50]};
`;

const Th = styled.th`
  padding: 11px 14px;
  text-align: ${props => props.align || 'left'};
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid ${colors.neutral[200]};
  white-space: nowrap;
  user-select: none;

  ${props => props.sortable && `
    cursor: pointer;
    transition: color 0.2s;

    &:hover {
      color: ${colors.primary.main};
    }
  `}
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid ${colors.neutral[100]};
  transition: background 0.15s;

  &:hover {
    background: ${colors.neutral[50]};
  }
`;

const TotalsRow = styled.tr`
  background: ${colors.primary.main}08;
  border-top: 2px solid ${colors.primary.main};

  td {
    font-weight: 700 !important;
    font-size: 13px !important;
    padding: 12px 14px !important;
  }
`;

const Td = styled.td`
  padding: 10px 14px;
  font-size: 13px;
  color: ${colors.text.primary};
  text-align: ${props => props.align || 'left'};
  white-space: nowrap;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AmountTd = styled(Td)`
  font-family: 'SF Mono', 'Consolas', monospace;
  font-size: 12px;
  color: ${props => props.$highlight ? colors.text.primary : colors.text.secondary};
  font-weight: ${props => props.$highlight ? 600 : 400};
`;

const DocLink = styled.span`
  color: ${colors.primary.main};
  font-weight: 600;
  cursor: pointer;
  font-size: 12px;
  font-family: 'SF Mono', 'Consolas', monospace;
  transition: color 0.2s;
  margin-right: 6px;

  &:hover {
    color: ${colors.primary.dark};
    text-decoration: underline;
  }
`;

const TranTypeBadge = styled.span`
  display: inline-block;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.3px;
  background: ${props => props.$type === 'PR' ? `${colors.error.main}15` : `${colors.primary.main}12`};
  color: ${props => props.$type === 'PR' ? colors.error.main : colors.primary.main};
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  background: ${props => STATUS_COLORS[props.$status]?.bg || `${colors.neutral[400]}15`};
  color: ${props => STATUS_COLORS[props.$status]?.text || colors.text.secondary};
`;

// ── Empty / Loading ──

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${colors.text.secondary};
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  color: ${colors.neutral[300]};
  margin-bottom: 16px;
`;

const EmptyText = styled.p`
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 6px;
  color: ${colors.text.primary};
`;

const EmptySubText = styled.p`
  font-size: 13px;
  color: ${colors.text.tertiary};
`;

const LoadingSpinner = styled.div`
  width: 36px;
  height: 36px;
  border: 3px solid ${colors.neutral[200]};
  border-top: 3px solid ${colors.primary.main};
  border-radius: 50%;
  margin: 0 auto 16px auto;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

export default PurchaseSummary;
